/** Cloze diagram step — interactive blanked-out nodes in a Mermaid diagram. */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { RotateCcw, Delete } from 'lucide-react';
import mermaid from 'mermaid';
import { useSession } from '../SessionContext';
import { AnimatedKbd, useShortcutOverlay } from '../components/ShortcutOverlay';
import { useKeyboardRipple, RippleFlash } from '../components/KeyboardRipple';
import {
  PromptRenderer,
  type TiptapNode,
  type MediaItem,
} from '@/shared/components/PromptRenderer';
import { normaliseMediaRefs } from '../utils/normaliseMediaRefs';
import { StepShell } from './StepShell';
import { FeedbackDisplay } from './FeedbackDisplay';
import { buildMermaidThemeVariables, adaptDiagramDirection } from '@/shared/utils/mermaidTheme';
import { ScrollableOverflow } from '@/shared/components/ScrollableOverflow';
import type { ClozeDiagramConfig } from '../types';

// ─── Types ──────────────────────────────────────────────────────────────────

type ClozeDiagramBlank = ClozeDiagramConfig['blanks'][number];

interface DiagramJson {
  syntax: string;
  diagramType: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────

const EMPTY_BLANKS: ClozeDiagramBlank[] = [];
const EMPTY_OPTIONS: string[] = [];
const EMPTY_BLANKS_MAP: Record<string, string> = {};

let diagramIdCounter = 0;

function getCssVar(name: string, source?: Element | null): string {
  const scopedValue = source ? getComputedStyle(source).getPropertyValue(name).trim() : '';
  if (scopedValue) return scopedValue;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

// ─── SVG DOM helpers ────────────────────────────────────────────────────────

/**
 * Find the Mermaid node group element for a given nodeId.
 * Mermaid v10+ generates elements with ids like `flowchart-<nodeId>-<n>`.
 */
function findNodeGroup(container: HTMLDivElement, nodeId: string): Element | null {
  return (
    container.querySelector(`[id*="flowchart-${nodeId}-"]`) ??
    container.querySelector(`[id*="${nodeId}"]`)
  );
}

/**
 * Find the shape element (rect, polygon, circle, ellipse, path) inside a node group.
 * Used to modify stroke for active/graded states.
 */
function findShapeElement(group: Element): SVGElement | null {
  for (const tag of ['rect', 'polygon', 'circle', 'ellipse', 'path']) {
    const el = group.querySelector(tag);
    if (el) return el as SVGElement;
  }
  return null;
}

/**
 * Find the label content element inside a node group.
 * Mermaid v10+ uses foreignObject > div for HTML labels.
 * Falls back to text element for SVG labels.
 */
function findLabelElement(
  group: Element
): { type: 'html'; element: HTMLElement } | { type: 'svg'; element: SVGTextElement } | null {
  const foreignObj = group.querySelector('foreignObject');
  if (foreignObj) {
    const div = foreignObj.querySelector('div');
    if (div) return { type: 'html', element: div };
  }
  const textEl = group.querySelector('text');
  if (textEl) return { type: 'svg', element: textEl };
  return null;
}

/**
 * Resize the foreignObject to fill its parent node's shape dimensions.
 * Mermaid sizes foreignObjects to fit the original label text, but blank nodes
 * have short placeholders ("___") so the foreignObject is tiny. This reads the
 * shape (rect/polygon) bounding box and resizes the foreignObject to match.
 */
/**
 * Prepare a blank node's foreignObject for interactive content.
 * Keeps Mermaid's positioning intact — only sets overflow and centering on the inner div.
 * The underscore placeholders already sized the node correctly during render.
 */
function prepareBlankNode(group: Element): void {
  const foreignObj = group.querySelector('foreignObject');
  if (!foreignObj) return;

  // Allow content to overflow if needed (e.g. graded text longer than placeholder)
  (foreignObj as SVGForeignObjectElement).style.overflow = 'visible';

  // Center content (input or text) inside the existing foreignObject
  const div = foreignObj.querySelector('div') as HTMLElement | null;
  if (div) {
    div.style.display = 'flex';
    div.style.alignItems = 'center';
    div.style.justifyContent = 'center';
    div.style.width = '100%';
    div.style.height = '100%';
    div.style.overflow = 'visible';
  }
}

/**
 * Set label text safely using DOM methods (no innerHTML for user-provided values).
 */
function setLabelText(
  label: { type: 'html'; element: HTMLElement } | { type: 'svg'; element: SVGTextElement },
  text: string,
  style?: Partial<CSSStyleDeclaration>
): void {
  if (label.type === 'html') {
    const span = document.createElement('span');
    span.textContent = text;
    if (style) Object.assign(span.style, style);
    label.element.textContent = '';
    label.element.appendChild(span);
  } else {
    label.element.textContent = text;
  }
}

// ─── Main component ─────────────────────────────────────────────────────────

export function ClozeDiagramStep() {
  const { currentStep, state, dispatch } = useSession();
  const { showShortcuts } = useShortcutOverlay();
  const { rippleIndex, rippleKey } = useKeyboardRipple();

  const containerRef = useRef<HTMLDivElement>(null);
  const reactId = useId();
  const diagramId = useRef(`mermaid-cloze-${reactId.replace(/:/g, '')}-${++diagramIdCounter}`);

  const snapshot = currentStep?.stepSnapshotPublicJson;
  const promptDoc = snapshot?.prompt as TiptapNode | undefined;
  const mediaItems: MediaItem[] | null = normaliseMediaRefs(currentStep?.mediaRefsJson);
  const config = (snapshot?.config ?? null) as unknown as ClozeDiagramConfig | null;
  const blanks = config?.blanks ?? EMPTY_BLANKS;
  const mode = config?.mode ?? 'free_text';
  const isMultipleChoice = mode === 'multiple_choice';

  // Access diagramJson from the step (not in shared type yet)
  const diagramJson = (currentStep as unknown as Record<string, unknown> | undefined)
    ?.diagramJson as DiagramJson | null | undefined;
  const diagramSyntax = diagramJson?.syntax ?? '';

  const answer = state.answer?.type === 'cloze_diagram' ? state.answer : null;
  const blanksMap = answer?.blanks ?? EMPTY_BLANKS_MAP;
  const phase = state.phase;
  const result = state.result;
  const blankResults = result?.blankResults;
  const isDisabled = phase === 'submitting' || phase === 'grading';
  const isGraded = phase === 'graded';

  // Blanks in position order (for sequential fill logic)
  const blanksInOrder = useMemo(
    () => [...blanks].sort((a, b) => a.position - b.position),
    [blanks]
  );

  // MC options pool: correct answers + distractors (pre-shuffled by server)
  const allOptions = config?.options ?? EMPTY_OPTIONS;

  // Track which options are used (MC)
  const usedOptionCounts = useMemo(() => {
    const counts = new Map<string, number>();
    if (!isMultipleChoice) return counts;
    for (const b of blanksInOrder) {
      const val = blanksMap[b.id];
      if (val) counts.set(val, (counts.get(val) || 0) + 1);
    }
    return counts;
  }, [isMultipleChoice, blanksInOrder, blanksMap]);

  // Available pool: options minus used
  const availablePool = useMemo(() => {
    if (!isMultipleChoice) return [];
    const remaining = new Map(usedOptionCounts);
    return allOptions.map((opt, i) => {
      const usedCount = remaining.get(opt) || 0;
      if (usedCount > 0) {
        remaining.set(opt, usedCount - 1);
        return { text: opt, index: i, isUsed: true };
      }
      return { text: opt, index: i, isUsed: false };
    });
  }, [isMultipleChoice, allOptions, usedOptionCounts]);

  const hasAnyAnswer = blanksInOrder.some((b) => (blanksMap[b.id] || '').length > 0);
  const [hoveredBubble, setHoveredBubble] = useState<number | null>(null);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleBlankChange = useCallback(
    (blankId: string, value: string) => {
      if (isDisabled || isGraded) return;
      dispatch({
        type: 'UPDATE_ANSWER',
        answer: { type: 'cloze_diagram', blanks: { ...blanksMap, [blankId]: value } },
      });
    },
    [isDisabled, isGraded, dispatch, blanksMap]
  );

  // MC: tap a bubble to fill the next empty blank
  const handleBubbleClick = useCallback(
    (optionText: string) => {
      if (isDisabled || isGraded) return;
      for (const b of blanksInOrder) {
        if (!blanksMap[b.id]) {
          handleBlankChange(b.id, optionText);
          return;
        }
      }
    },
    [isDisabled, isGraded, blanksInOrder, blanksMap, handleBlankChange]
  );

  // MC: tap a filled node to clear it
  const handleNodeClear = useCallback(
    (blankId: string) => {
      if (isDisabled || isGraded) return;
      handleBlankChange(blankId, '');
    },
    [isDisabled, isGraded, handleBlankChange]
  );

  // MC: undo last filled blank
  const handleUndo = useCallback(() => {
    if (isDisabled || isGraded) return;
    for (let i = blanksInOrder.length - 1; i >= 0; i--) {
      if (blanksMap[blanksInOrder[i].id]) {
        handleBlankChange(blanksInOrder[i].id, '');
        return;
      }
    }
  }, [isDisabled, isGraded, blanksInOrder, blanksMap, handleBlankChange]);

  // Reset all blanks
  const handleReset = useCallback(() => {
    if (isDisabled || isGraded) return;
    const cleared: Record<string, string> = {};
    for (const b of blanksInOrder) {
      cleared[b.id] = '';
    }
    dispatch({ type: 'UPDATE_ANSWER', answer: { type: 'cloze_diagram', blanks: cleared } });
  }, [isDisabled, isGraded, blanksInOrder, dispatch]);

  // ── Render diagram & manipulate SVG DOM ─────────────────────────────────

  // Store handlers in refs so the effect can access latest without re-running
  const handleNodeClearRef = useRef(handleNodeClear);
  handleNodeClearRef.current = handleNodeClear;
  const handleBlankChangeRef = useRef(handleBlankChange);
  handleBlankChangeRef.current = handleBlankChange;

  // Counter to force Effect 2 to re-run after Effect 1 renders the SVG
  const [svgRenderCount, setSvgRenderCount] = useState(0);

  // ── Effect 1: Render Mermaid SVG (only when syntax changes) ──────────────
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !diagramSyntax) return;

    let cancelled = false;

    async function renderSvg() {
      if (!container || cancelled) return;

      const tokenSource = container.closest('[data-interface]') ?? container;

      mermaid.initialize({
        startOnLoad: false,
        theme: 'base',
        themeVariables: buildMermaidThemeVariables(tokenSource),
        securityLevel: 'strict',
        flowchart: { htmlLabels: true, useMaxWidth: false, curve: 'basis' },
      });

      try {
        // Replace blank node labels with underscore placeholders sized to the answer.
        // Mermaid sizes nodes based on label text — underscores won't be collapsed.
        // Effect 2 immediately replaces them with inputs/text so the learner never sees them.
        let presizedSyntax = diagramSyntax;
        for (const blank of blanks) {
          const len = Math.max(blank.answerLength ?? 8, 14) + 4; // min 14 to fit "Type answer..."
          const placeholder = '_'.repeat(len);
          const esc = blank.nodeId.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // Match quoted: B["___"] or B["?"] and unquoted: B[___]
          presizedSyntax = presizedSyntax
            .replace(new RegExp(`(${esc}\\[")(.*?)("])`, 'g'), `$1${placeholder}$3`)
            .replace(new RegExp(`(${esc}\\[)((?!")[^\\]]*)(])`, 'g'), `$1${placeholder}$3`);
        }
        const adaptedSyntax = adaptDiagramDirection(presizedSyntax);
        const { svg } = await mermaid.render(diagramId.current, adaptedSyntax);
        if (cancelled || !container) return;

        // Safe: mermaid securityLevel:'strict' sanitises SVG output
        container.textContent = '';
        const wrapper = document.createElement('div');
        wrapper.innerHTML = svg;
        const svgEl = wrapper.querySelector('svg');
        if (svgEl) {
          // Don't constrain SVG width — let it render at natural size.
          // The container has overflow-x: auto for horizontal scroll.
          svgEl.style.height = 'auto';
          container.appendChild(svgEl);
        }

        // Resize blank nodes to fit expected answer length
        for (const blank of blanks) {
          const group = findNodeGroup(container, blank.nodeId);
          if (group) prepareBlankNode(group);
        }

        // Trigger Effect 2 to update blank node DOM
        if (!cancelled) setSvgRenderCount((c) => c + 1);
      } catch {
        if (cancelled) return;
        container.textContent = '';
        const errorDiv = document.createElement('div');
        Object.assign(errorDiv.style, {
          padding: '12px',
          color: 'var(--f-error)',
          fontSize: 'var(--f-text-sm)',
          fontFamily: 'var(--f-font-sans)',
        });
        errorDiv.textContent = 'Failed to render diagram';
        container.appendChild(errorDiv);
      }
    }

    void renderSvg();
    return () => {
      cancelled = true;
    };
  }, [diagramSyntax, blanks]);

  // ── Effect 2: Update blank node DOM (runs after SVG render + on state changes) ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !container.querySelector('svg')) return;

    const tokenSource = container.closest('[data-interface]') ?? container;
    const accentColor = getCssVar('--f-accent', tokenSource) || '#6366f1';
    const successColor = getCssVar('--f-success', tokenSource) || '#22c55e';
    const errorColor = getCssVar('--f-error', tokenSource) || '#ef4444';
    const fgMuted = getCssVar('--f-fg-muted', tokenSource) || '#64748b';
    const fgOnAccent = getCssVar('--f-fg-on-accent', tokenSource) || '#fff';
    const fontSans = getCssVar('--f-font-sans', tokenSource) || 'Plus Jakarta Sans, sans-serif';
    const fgColor = getCssVar('--f-fg', tokenSource) || '#0f172a';
    const borderStrong = getCssVar('--f-border-strong', tokenSource) || '#94a3b8';

    // Find active blank
    let activeBlankNodeId: string | null = null;
    if (!isGraded && !isDisabled) {
      for (const b of blanksInOrder) {
        if (!blanksMap[b.id]) {
          activeBlankNodeId = b.nodeId;
          break;
        }
      }
    }

    for (const blank of blanks) {
      const group = findNodeGroup(container, blank.nodeId);
      if (!group) continue;

      const label = findLabelElement(group);
      const shape = findShapeElement(group);
      const value = blanksMap[blank.id] ?? '';
      const blankResult = blankResults?.[blank.id];
      const isActive = blank.nodeId === activeBlankNodeId;

      // Reset shape styles
      if (shape) {
        shape.style.stroke = '';
        shape.style.strokeWidth = '';
        shape.style.strokeDasharray = '';
        shape.style.fill = '';
        shape.onclick = null;
        (shape as unknown as HTMLElement).style.cursor = '';
      }

      // ── Graded state ──
      if (isGraded && blankResult) {
        const color = blankResult.isCorrect ? successColor : errorColor;
        if (shape) {
          shape.style.stroke = color;
          shape.style.strokeWidth = '2.5';
        }
        if (label) {
          // Allow overflow for long answer text
          const fo = group.querySelector('foreignObject');
          if (fo) (fo as SVGForeignObjectElement).style.overflow = 'visible';

          if (blankResult.isCorrect) {
            setLabelText(label, value || '\u2014', { fontWeight: '600', color });
          } else if (label.type === 'html') {
            label.element.textContent = '';
            const w = document.createElement('span');
            Object.assign(w.style, {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '2px',
              whiteSpace: 'nowrap',
            });
            const wrong = document.createElement('span');
            wrong.textContent = value || '\u2014';
            Object.assign(wrong.style, {
              fontWeight: '500',
              color,
              textDecoration: 'line-through',
              fontSize: '0.85em',
            });
            const correct = document.createElement('span');
            correct.textContent = blankResult.correctAnswer;
            Object.assign(correct.style, {
              fontSize: '0.75em',
              fontWeight: '600',
              color: successColor,
            });
            w.appendChild(wrong);
            w.appendChild(correct);
            label.element.appendChild(w);
          } else {
            label.element.textContent = `${value} \u2192 ${blankResult.correctAnswer}`;
          }
        }
        continue;
      }

      // ── MC mode ──
      if (isMultipleChoice) {
        if (shape) {
          if (isActive) {
            shape.style.stroke = accentColor;
            shape.style.strokeWidth = '2.5';
            shape.style.strokeDasharray = '6,3';
          } else if (!value) {
            // Empty, non-active blank: dashed border
            shape.style.strokeDasharray = '6,3';
            shape.style.stroke = borderStrong;
          } else if (value) {
            shape.style.stroke = accentColor;
            shape.style.strokeWidth = '2';
          }
        }
        if (label) {
          if (value) {
            setLabelText(label, value, { fontWeight: '600', color: fgOnAccent, cursor: 'pointer' });
            if (label.type === 'html') {
              label.element.style.cursor = 'pointer';
              const blankId = blank.id;
              label.element.onclick = () => handleNodeClearRef.current(blankId);
            }
            if (shape) {
              shape.style.fill = accentColor;
              const blankId = blank.id;
              (shape as unknown as HTMLElement).style.cursor = 'pointer';
              shape.onclick = () => handleNodeClearRef.current(blankId);
            }
          } else {
            setLabelText(label, '?', { fontWeight: '500', color: fgMuted });
          }
        }
        continue;
      }

      // ── Free-text mode ── (no sequential highlighting — user clicks any blank)

      // Mark blank nodes with dashed border
      if (shape) {
        shape.style.strokeDasharray = '6,3';
        shape.style.stroke = borderStrong;
      }

      if (label && label.type === 'html') {
        // Only create input if one doesn't already exist (avoids destroying on re-run)
        let input = label.element.querySelector('input') as HTMLInputElement | null;
        if (!input) {
          label.element.textContent = '';
          input = document.createElement('input');
          input.type = 'text';
          input.placeholder = 'Type answer...';
          input.setAttribute('aria-label', `Blank ${blank.position + 1} text input`);

          Object.assign(input.style, {
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            padding: '4px 8px',
            border: 'none',
            borderRadius: '0',
            background: 'transparent',
            color: fgColor,
            fontFamily: fontSans,
            fontSize: 'inherit',
            fontWeight: '500',
            outline: 'none',
            textAlign: 'center',
          });

          const blankId = blank.id;
          input.addEventListener('input', (e) => {
            const target = e.target as HTMLInputElement;
            handleBlankChangeRef.current(blankId, target.value);
          });

          label.element.appendChild(input);
        }

        // Update input state without recreating it
        if (input.value !== value) input.value = value;
        input.disabled = isDisabled || isGraded;
      } else if (label) {
        label.element.textContent = value || '?';
      }
    }

    // MC mode: scroll the active blank node into view
    if (isMultipleChoice && activeBlankNodeId && container) {
      const activeGroup = findNodeGroup(container, activeBlankNodeId);
      if (activeGroup) {
        // Find the scroll container (ScrollableOverflow's inner div)
        const scrollContainer =
          container.closest('[style*="overflow"]')?.querySelector('[style*="overflow"]') ??
          container.parentElement;
        if (scrollContainer) {
          const groupRect = (activeGroup as HTMLElement).getBoundingClientRect();
          const scrollRect = scrollContainer.getBoundingClientRect();
          // Only scroll if the node is not fully visible
          if (groupRect.left < scrollRect.left || groupRect.right > scrollRect.right) {
            (activeGroup as HTMLElement).scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center',
            });
          }
        }
      }
    }
  }, [
    blanks,
    blanksInOrder,
    blanksMap,
    blankResults,
    isMultipleChoice,
    isDisabled,
    isGraded,
    svgRenderCount,
  ]);

  if (!currentStep || !snapshot || !diagramSyntax) return null;

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <StepShell>
      {promptDoc && (
        <div style={{ opacity: isDisabled ? 0.7 : 1 }}>
          <PromptRenderer doc={promptDoc} media={mediaItems} />
        </div>
      )}
      <div style={{ padding: 'var(--f-space-6) 0', fontFamily: 'var(--f-font-sans)' }}>
        <p
          style={{
            fontSize: 'var(--f-text-sm)',
            color: 'var(--f-fg-muted)',
            marginBottom: 'var(--f-space-2)',
          }}
        >
          {isMultipleChoice ? 'Tap options to fill the blank nodes' : 'Fill in the blank nodes'}
        </p>

        {/* Diagram container — stays in step width, scrolls horizontally if needed */}
        <ScrollableOverflow>
          <div
            ref={containerRef}
            style={{
              opacity: isDisabled ? 0.7 : 1,
              transition: 'opacity 0.2s ease',
            }}
          />
        </ScrollableOverflow>
      </div>

      {/* MC: bubble option pool */}
      {isMultipleChoice && allOptions.length > 0 && !isGraded && (
        <div style={{ marginTop: 'var(--f-space-6)' }}>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Available options">
            {availablePool.map((opt, i) => (
              <button
                key={opt.index}
                type="button"
                disabled={isDisabled || opt.isUsed}
                onClick={() => handleBubbleClick(opt.text)}
                onMouseEnter={() => !opt.isUsed && !isDisabled && setHoveredBubble(opt.index)}
                onMouseLeave={() => setHoveredBubble(null)}
                className="inline-flex items-center rounded-xl font-medium cursor-pointer active:scale-[0.97] focus:outline-none"
                style={{
                  padding: 'var(--f-space-2) var(--f-space-4)',
                  fontSize: 'var(--f-text-base)',
                  fontFamily: 'var(--f-font-sans)',
                  border: '2px solid',
                  borderColor: opt.isUsed
                    ? 'transparent'
                    : hoveredBubble === opt.index
                      ? 'var(--f-accent)'
                      : 'var(--f-border-strong)',
                  backgroundColor: opt.isUsed ? 'var(--f-bg-sunken)' : 'var(--f-bg-raised)',
                  color: opt.isUsed ? 'var(--f-fg-muted)' : 'var(--f-fg)',
                  opacity: opt.isUsed ? 0.4 : isDisabled ? 0.5 : 1,
                  cursor: opt.isUsed || isDisabled ? 'not-allowed' : 'pointer',
                  transition:
                    'border-color 0.3s ease, background-color 0.3s ease, color 0.3s ease, opacity 0.4s ease',
                  position: 'relative',
                }}
                aria-label={opt.isUsed ? `${opt.text} (used)` : opt.text}
              >
                <RippleFlash active={rippleIndex === opt.index} rippleKey={rippleKey} />
                {opt.text}
                <AnimatedKbd show={!isGraded && !opt.isUsed && showShortcuts} position="inline">
                  {i + 1}
                </AnimatedKbd>
              </button>
            ))}
          </div>

          {/* Undo & Reset */}
          <AnimatePresence>
            {hasAnyAnswer && (
              <motion.div
                className="mt-3 flex items-center gap-2"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={handleUndo}
                  className="flex items-center gap-1 rounded-lg px-3 py-1.5 transition-opacity cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{
                    fontSize: 'var(--f-text-sm)',
                    color: 'var(--f-fg-secondary)',
                    fontFamily: 'var(--f-font-sans)',
                  }}
                >
                  <Delete size={14} />
                  Undo
                </button>
                <button
                  type="button"
                  disabled={isDisabled}
                  onClick={handleReset}
                  className="flex items-center gap-1 rounded-lg px-3 py-1.5 transition-opacity cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{
                    fontSize: 'var(--f-text-sm)',
                    color: 'var(--f-fg-secondary)',
                    fontFamily: 'var(--f-font-sans)',
                  }}
                >
                  <RotateCcw size={14} />
                  Reset
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* FT: Reset button */}
      {!isMultipleChoice && blanks.length > 0 && !isGraded && (
        <AnimatePresence>
          {hasAnyAnswer && (
            <motion.div
              className="flex items-center gap-2"
              style={{ marginTop: 'var(--f-space-4)' }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <button
                type="button"
                disabled={isDisabled}
                onClick={handleReset}
                className="flex items-center gap-1 rounded-lg px-3 py-1.5 transition-opacity cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                style={{
                  fontSize: 'var(--f-text-sm)',
                  color: 'var(--f-fg-secondary)',
                  fontFamily: 'var(--f-font-sans)',
                }}
              >
                <RotateCcw size={14} />
                Reset
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {isGraded && result && <FeedbackDisplay result={result} />}
    </StepShell>
  );
}
